import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowLeft } from "lucide-react";
import api from "../../lib/api";

/**
 * FromChatPill — Wave 19 #94.
 *
 * Renders a small "← from chat: <thread title>" pill at the top of
 * entity detail pages (Task / Job card / Ticket / Meeting / Procurement)
 * when the entity was created via the right-click conversion flow.
 *
 * Props:
 *   - threadId: source_chat_thread_id from the entity
 *   - messageId: source_chat_message_id (used as anchor; future scroll-to)
 *
 * Self-fetches the thread for its title (cheap — single GET) so callers
 * just pass the two IDs.
 */
export default function FromChatPill({ threadId, messageId }) {
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!threadId) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/chats/${threadId}`);
        if (!alive) return;
        setTitle(data?.title || "Chat");
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 403) {
          setDenied(true);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [threadId]);

  if (!threadId) return null;
  if (loading) {
    return (
      <div
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#647087] bg-white/[0.03] border border-white/10 rounded-full px-2.5 py-1"
        data-testid="from-chat-pill-loading"
      >
        <MessageCircle className="w-3 h-3" />
        Loading source chat…
      </div>
    );
  }
  if (denied) {
    // User isn't a member of the source chat — show a muted pill that
    // states the source exists but doesn't link.
    return (
      <div
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#647087] bg-white/[0.03] border border-white/10 rounded-full px-2.5 py-1"
        data-testid="from-chat-pill-denied"
        title="Created from a chat thread you're not a member of"
      >
        <MessageCircle className="w-3 h-3" />
        From a private chat
      </div>
    );
  }
  return (
    <Link
      to={`/chat/${threadId}${messageId ? `#${messageId}` : ""}`}
      className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 rounded-full bg-[#1fb6c1]/10 border border-[#1fb6c1]/35 text-[#7fe5ec] hover:bg-[#1fb6c1]/20 hover:text-white transition"
      data-testid="from-chat-pill"
    >
      <ArrowLeft className="w-3 h-3" />
      <MessageCircle className="w-3 h-3" />
      <span className="truncate max-w-[260px]">From chat: {title}</span>
    </Link>
  );
}
